import { Card, Divider, Stack, Switch, Table, Typography, styled } from '@healthinal/ui';
import { endOfYesterday, isAfter } from 'date-fns';
import { HTMLProps, useState } from 'react';
import { HeurekaMedicationStatement } from '../api/heurekaPatientDetails';
import { theme } from '../theme';
import { formatDate } from '../utils/date';
import { ExpandableCell } from './ExpandableCell';
import { TabularNumbers } from './TabularNumbers';

interface Props {
  title: string;
  medicationStatements: HeurekaMedicationStatement[];
}

export default function MedicationTable({ medicationStatements, title }: Props) {
  const [showInactiveMedication, setShowInactiveMedication] = useState(false);

  const filteredAndSortedMedicationStatements = medicationStatements
    .filter((medication) => isMedicationActive(medication) || showInactiveMedication)
    .sort(sortMedications);

  const isInactiveMedicationAvailable = medicationStatements.some((m) => !isMedicationActive(m));

  return (
    <StyledCard>
      <CardHeader>
        <Typography level="title-md" fontWeight={600}>
          {title}
        </Typography>
        {isInactiveMedicationAvailable && (
          <>
            <Divider orientation="vertical" />
            <Typography
              component="label"
              level="body-sm"
              textColor="text.secondary"
              startDecorator={
                <Switch
                  checked={showInactiveMedication}
                  onChange={(e) => setShowInactiveMedication(e.target.checked)}
                  sx={{ mr: 1 }}
                />
              }>
              Frühere Medikamente anzeigen
            </Typography>
          </>
        )}
      </CardHeader>
      <CardContent>
        <StyledTable noWrap>
          <thead>
            <tr>
              <th>Medikament</th>
              <th style={{ width: 232 }}>Dosierung</th>
              <th>Bemerkung</th>
              <th style={{ width: 232 }}>Dauer</th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedMedicationStatements.map((medication, index) => {
              return <MedicationRow medication={medication} key={index} />;
            })}
          </tbody>
        </StyledTable>
        {showInactiveMedication && (
          <Typography level="body-sm" textAlign={'center'} sx={{ mt: 4 }}>
            Frühere Medikamente werden nur bis zu einem Jahr rückwirkend angezeigt.
          </Typography>
        )}
      </CardContent>
    </StyledCard>
  );
}

function activeMedicationFirst(a: HeurekaMedicationStatement, b: HeurekaMedicationStatement) {
  const aActive = isMedicationActive(a);
  const bActive = isMedicationActive(b);
  if (aActive === bActive) return 0;
  return aActive ? -1 : 1;
}

function dateDescending(a?: Date, b?: Date, prioritizeUndefinedDate = false) {
  if (b && a) return b.getTime() - a.getTime();
  else if (!b && !a) return 0;
  else if (prioritizeUndefinedDate) return a ? 1 : -1;
  else return a ? -1 : 1;
}

function nameAscending(a: HeurekaMedicationStatement, b: HeurekaMedicationStatement) {
  return a.medicationName.localeCompare(b.medicationName);
}

function sortMedications(a: HeurekaMedicationStatement, b: HeurekaMedicationStatement) {
  return (
    activeMedicationFirst(a, b) ||
    dateDescending(a.from, b.from) ||
    dateDescending(a.until, b.until, true) ||
    nameAscending(a, b)
  );
}

const isMedicationActive = (medication: HeurekaMedicationStatement) =>
  // endOfYesterday() so medication ending today is still considered active
  !medication.until || isAfter(medication.until, endOfYesterday());

const StyledCard = styled(Card)`
  --Card-padding: 0;
  overflow: hidden; // for rounded corners
  margin-bottom: 40px;
`;

const CardHeader = styled('div')`
  padding: 24px 32px;
  border-bottom: solid 1px ${theme.vars.palette.divider};
  display: flex;
  gap: 24px;
`;

const CardContent = styled('div')`
  padding: 0 16px 16px;
`;

const StyledTable = styled(Table)`
  --Table-headerUnderlineThickness: 1px;
  --TableCell-paddingX: 16px;
  --TableCell-paddingY: 9px;

  thead th {
    ${theme.typography['title-sm']};
    vertical-align: middle;
  }

  td {
    vertical-align: top;
  }

  td,
  th {
    &:not(:first-of-type) {
      border-left: solid 1px var(--TableCell-borderColor);
    }
  }
`;

interface MedicationRowProps extends HTMLProps<HTMLTableRowElement> {
  medication: HeurekaMedicationStatement;
}

const MedicationRow = ({ medication, ...trProps }: MedicationRowProps) => {
  const isActive = isMedicationActive(medication);
  const [open, setOpen] = useState(false);

  return (
    <tr {...trProps}>
      <ExpandableCell
        isOpen={open}
        onCellClick={() => setOpen(!open)}
        style={{ fontStyle: isActive ? undefined : 'italic' }}>
        {medication.medicationName}
      </ExpandableCell>
      <ExpandableCell isOpen={open} onCellClick={() => setOpen(!open)}>
        <TabularNumbers>{medication.dosingSchedule?.join(open ? '\n' : ', ')}</TabularNumbers>
      </ExpandableCell>
      <ExpandableCell isOpen={open} onCellClick={() => setOpen(!open)}>
        {medication.comments?.join(open ? '\n' : ', ')}
      </ExpandableCell>
      <ExpandableCell isOpen={open} onCellClick={() => setOpen(!open)}>
        {(medication.from || medication.until) && (
          <Stack direction="row" alignItems="flex-end" gap={1}>
            <TabularNumbers>{formatDate(medication.from)}</TabularNumbers>
            <Typography fontSize={12} textColor={'text.tertiary'}>
              bis
            </Typography>
            <TabularNumbers>{formatDate(medication.until)}</TabularNumbers>
          </Stack>
        )}
      </ExpandableCell>
    </tr>
  );
};
